/**
 * CustomerSendEmailModal
 *
 * Thin wrapper around BulkEmailModal that provides customer-specific
 * props (recipient list, merge-data builder, callback event).
 *
 * Merge data (including transaction table HTML) is fetched from the
 * backend via POST /api/v1/email/merge-data so the frontend no longer
 * needs to build it client-side.
 *
 * The exported interface is unchanged so page-level imports keep working.
 */

import { useMemo, useCallback, useRef } from "react";
import { useLogBulkCustomerEmail } from "@/hooks/useLogBulkCustomerEmail";
import {
  BulkEmailModal,
  type BulkEmailRecipient,
  type MergeDataResult,
  type BulkEmailModalProps,
} from "@/components/shared/bulk-email-modal";
import {
  useEmailMergeData,
  type EmailMergeRecipientResponse,
} from "@/hooks/useEmailMergeData";
import { resolvePlaceholders } from "@/lib/email-placeholder-utils";

// ─── Types (re-exported for backward compatibility) ──────────────────────────

export interface CustomerEmailRecipient {
  customerId: string;
  accountNumber: string;
  customerName: string;
  toEmails: string[];
  ccEmails: string[];
}

export interface CustomerSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** For single customer mode */
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  accountNumber?: string;
  /** For bulk mode */
  isBulkEmail?: boolean;
  bulkCustomers?: Array<{
    id: string;
    name: string;
    accountNumber: string;
    contact: string;
    phone?: string;
    outstanding?: number;
    overdue?: number;
    creditLimit?: number;
    currency?: string;
    primaryContactFirstName?: string;
    primaryContactSurname?: string;
    creditControllerName?: string;
    creditControllerEmail?: string;
  }>;
  onSuccess?: () => void;
  /** Override the step indicator — pass `{ startAt: 2, total: 4 }` from Send Statements flow */
  stepOverride?: { startAt: number; total: number };
  /**
   * Optional list of allowed template type codes (e.g. ["email"] or ["statement"]).
   * Defaults to ["email"] when not provided.
   */
  allowedTemplateTypes?: string[];
  /** Per-recipient attachment fetcher forwarded to BulkEmailModal */
  fetchAttachments?: BulkEmailModalProps["fetchAttachments"];
  /** Attach checkbox label forwarded to BulkEmailModal */
  attachLabel?: string;
  /** Loading text shown while preparing attachments forwarded to BulkEmailModal */
  preparingLabel?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function CustomerSendEmailModal({
  open,
  onOpenChange,
  customerId,
  customerName = "",
  customerEmail = "",
  accountNumber = "",
  isBulkEmail = false,
  bulkCustomers = [],
  onSuccess,
  stepOverride,
  allowedTemplateTypes = ["email"],
  fetchAttachments,
  attachLabel,
  preparingLabel,
}: Readonly<CustomerSendEmailModalProps>) {
  const mergeDataMutation = useEmailMergeData();
  const logBulkCustomerEmail = useLogBulkCustomerEmail();

  // Cache backend merge data response keyed by recipientId
  const mergeDataCache = useRef<Map<string, EmailMergeRecipientResponse>>(new Map());

  // Convert caller data into the generic BulkEmailRecipient[] format
  const recipients: BulkEmailRecipient[] = useMemo(() => {
    if (isBulkEmail) {
      return bulkCustomers.map((c) => ({
        id: c.id,
        name: c.name,
        accountNumber: c.accountNumber,
        defaultEmail: c.contact,
      }));
    }
    if (customerId) {
      return [
        {
          id: customerId,
          name: customerName,
          accountNumber,
          defaultEmail: customerEmail,
        },
      ];
    }
    return [];
  }, [isBulkEmail, bulkCustomers, customerId, customerName, accountNumber, customerEmail]);

  const handleAfterSend = useCallback(
    (payload: {
      recipients: Array<{
        recipientId: string;
        toEmails: string[];
        ccEmails: string[];
      }>;
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logBulkCustomerEmail.mutate({
        entries: payload.recipients.map((r) => {
          const cached = mergeDataCache.current.get(r.recipientId);
          const mergeData: Record<string, string> = {};
          if (cached) {
            for (const [k, v] of Object.entries(cached.mergeData)) {
              mergeData[k.toLowerCase()] = v;
            }
            if (cached.mergeDataHtml) {
              for (const [k, v] of Object.entries(cached.mergeDataHtml)) {
                mergeData[k.toLowerCase()] = v;
              }
            }
          }
          mergeData["email"] = r.toEmails[0] ?? "";

          const resolvedSubject = resolvePlaceholders(payload.subject, mergeData);
          const resolvedBody = resolvePlaceholders(payload.htmlBody, mergeData);

          return {
            customerId: Number(r.recipientId),
            toRecipients: r.toEmails,
            ccRecipients: r.ccEmails.length > 0 ? r.ccEmails : undefined,
            subject: resolvedSubject,
            htmlBody: resolvedBody,
            templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
            templateName: payload.templateName,
            isSubjectModified: payload.isSubjectModified,
            isBodyModified: payload.isBodyModified,
            attachOutstandingInvoices: payload.attachOutstandingInvoices,
          };
        }),
      });
    },
    [logBulkCustomerEmail],
  );

  const handleFetchMergeData = useCallback(
    async (subject: string, body: string): Promise<{
      unknownPlaceholders: string[];
      emptyDataPlaceholders: string[];
    }> => {
      const recipientEntries: Array<{
        recipientId: string;
        recipientEmail: string;
        customerId: number;
      }> = [];

      if (isBulkEmail) {
        for (const c of bulkCustomers) {
          recipientEntries.push({
            recipientId: c.id,
            recipientEmail: c.contact,
            customerId: Number(c.id),
          });
        }
      } else if (customerId) {
        recipientEntries.push({
          recipientId: customerId,
          recipientEmail: customerEmail,
          customerId: Number(customerId),
        });
      }

      if (recipientEntries.length === 0) return { unknownPlaceholders: [], emptyDataPlaceholders: [] };

      const response = await mergeDataMutation.mutateAsync({
        sourceType: "customer",
        subject,
        body,
        recipients: recipientEntries,
      });

      const cache = new Map<string, EmailMergeRecipientResponse>();
      for (const r of response.recipients) {
        cache.set(r.recipientId, r);
      }
      mergeDataCache.current = cache;

      return {
        unknownPlaceholders: response.unknownPlaceholders ?? [],
        emptyDataPlaceholders: response.emptyDataPlaceholders ?? [],
      };
    },
    [isBulkEmail, bulkCustomers, customerId, customerEmail, mergeDataMutation],
  );

  const handleBuildMergeData = useCallback(
    (recipient: BulkEmailRecipient, toEmail: string): MergeDataResult => {
      const cached = mergeDataCache.current.get(recipient.id);
      if (cached) {
        const mergeData = { ...cached.mergeData, email: toEmail };
        const mergeDataHtml = cached.mergeDataHtml ?? {};
        return { mergeData, mergeDataHtml };
      }

      return {
        mergeData: { email: toEmail },
        mergeDataHtml: {},
      };
    },
    [],
  );

  return (
    <BulkEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      recipients={recipients}
      buildMergeData={handleBuildMergeData}
      callbackEventName="creditcontrol:bulk-email-done"
      attachLabel={attachLabel ?? "Attach outstanding invoices"}
      preparingLabel={preparingLabel}
      fetchAttachments={fetchAttachments}
      onSuccess={onSuccess}
      stepOverride={stepOverride}
      onFetchMergeData={handleFetchMergeData}
      allowedCategoryTypes={["customers", "general"]}
      allowedTemplateTypes={allowedTemplateTypes}
      onAfterSend={handleAfterSend}
    />
  );
}
